import { CORAL_CATEGORIES } from '@/data/coralCategories';
import type {
  ImportRow,
  ColumnMapping,
  AutoFixResult,
  DateFormatPreference,
  TargetField,
} from '../types';

// ---------------------------------------------------------------------------
// Category synonym map: lowercase input → canonical category
// ---------------------------------------------------------------------------
const CATEGORY_SYNONYMS: Record<string, string> = {
  sps: 'SPS',
  lps: 'LPS',
  softie: 'Soft Corals',
  softies: 'Soft Corals',
  'soft coral': 'Soft Corals',
  'soft corals': 'Soft Corals',
  softs: 'Soft Corals',
  mushroom: 'Mushrooms',
  mushrooms: 'Mushrooms',
  shrooms: 'Mushrooms',
  zoa: 'Zoanthids/Palythoa',
  zoas: 'Zoanthids/Palythoa',
  zoanthid: 'Zoanthids/Palythoa',
  zoanthids: 'Zoanthids/Palythoa',
  palythoa: 'Zoanthids/Palythoa',
  paly: 'Zoanthids/Palythoa',
  palys: 'Zoanthids/Palythoa',
  anemone: 'Anemones',
  anemones: 'Anemones',
  nem: 'Anemones',
  nems: 'Anemones',
};

// Inputs that look like a category but are actually a subcategory.
// Maps lowercase input → { category, subcategory }
const RECLASSIFY_MAP: Record<string, { category: string; subcategory: string }> = {
  torch: { category: 'LPS', subcategory: 'Torches' },
  torches: { category: 'LPS', subcategory: 'Torches' },
  hammer: { category: 'LPS', subcategory: 'Hammers' },
  hammers: { category: 'LPS', subcategory: 'Hammers' },
  frogspawn: { category: 'LPS', subcategory: 'Frogspawn' },
  acropora: { category: 'SPS', subcategory: 'Acropora' },
  montipora: { category: 'SPS', subcategory: 'Montipora' },
  duncan: { category: 'LPS', subcategory: 'Duncans' },
  duncans: { category: 'LPS', subcategory: 'Duncans' },
};

// ---------------------------------------------------------------------------
// Subcategory abbreviation map: lowercase abbreviation → canonical subcategory
// ---------------------------------------------------------------------------
const SUBCATEGORY_ABBREVIATIONS: Record<string, string> = {
  acro: 'Acropora',
  acros: 'Acropora',
  monti: 'Montipora',
  montis: 'Montipora',
  stylo: 'Stylophora',
  stylos: 'Stylophora',
  blasto: 'Blastomussa',
  blastos: 'Blastomussa',
  gonni: 'Goniopora',
  gonio: 'Goniopora',
  ric: 'Ricordea',
  ricord: 'Ricordea',
  rics: 'Ricordea',
  pocil: 'Pocillopora',
  seriat: 'Seriatopora',
  cyph: 'Cyphastrea',
  lepto: 'Leptoseris',
  pav: 'Pavona',
  por: 'Porites',
};

// ---------------------------------------------------------------------------
// Status synonym map: lowercase input → canonical status
// ---------------------------------------------------------------------------
const STATUS_SYNONYMS: Record<string, string> = {
  alive: 'alive',
  living: 'alive',
  live: 'alive',
  healthy: 'alive',
  dead: 'deceased',
  deceased: 'deceased',
  died: 'deceased',
  lost: 'deceased',
  rip: 'deceased',
  sold: 'sold',
  traded: 'sold',
  given: 'sold',
  'given away': 'sold',
  fragged: 'sold',
  unknown: 'unknown',
  '?': 'unknown',
  '': 'unknown',
  n_a: 'unknown',
  na: 'unknown',
  'n/a': 'unknown',
};

// ---------------------------------------------------------------------------
// Name-based keyword → category/subcategory inference
// ---------------------------------------------------------------------------

// Keywords found in coral names that strongly signal a specific category.
// Checked as whole-word boundaries (case-insensitive).
// Format: keyword → { category, subcategory? }
const NAME_CATEGORY_KEYWORDS: Array<{
  keyword: string;
  category: string;
  subcategory?: string;
}> = [
  // Mushrooms (commonly miscategorised as Soft Corals)
  { keyword: 'mushroom', category: 'Mushrooms' },
  { keyword: 'shroom', category: 'Mushrooms' },
  { keyword: 'ricordea', category: 'Mushrooms', subcategory: 'Ricordea' },
  { keyword: 'rhodactis', category: 'Mushrooms', subcategory: 'Rhodactis' },
  { keyword: 'discosoma', category: 'Mushrooms', subcategory: 'Discosoma' },
  // Zoanthids
  { keyword: 'zoa', category: 'Zoanthids/Palythoa' },
  { keyword: 'zoanthid', category: 'Zoanthids/Palythoa' },
  { keyword: 'paly', category: 'Zoanthids/Palythoa', subcategory: 'Palythoas' },
  // Anemones (subcategories first — more specific matches before generic)
  { keyword: 'bubble tip', category: 'Anemones', subcategory: 'Bubble Tip' },
  { keyword: 'carpet', category: 'Anemones', subcategory: 'Carpet' },
  { keyword: 'anemone', category: 'Anemones' },
  { keyword: 'nem', category: 'Anemones' },
  // LPS with subcategory
  { keyword: 'torch', category: 'LPS', subcategory: 'Torches' },
  { keyword: 'hammer', category: 'LPS', subcategory: 'Hammers' },
  { keyword: 'frogspawn', category: 'LPS', subcategory: 'Frogspawn' },
  { keyword: 'duncan', category: 'LPS', subcategory: 'Duncans' },
  { keyword: 'chalice', category: 'LPS', subcategory: 'Chalices' },
  { keyword: 'candy cane', category: 'LPS', subcategory: 'Candy Cane' },
  { keyword: 'brain', category: 'LPS' },
  { keyword: 'scolymia', category: 'LPS', subcategory: 'Scolymia' },
  { keyword: 'goniopora', category: 'LPS', subcategory: 'Goniopora' },
  { keyword: 'elegance', category: 'LPS', subcategory: 'Elegance' },
  { keyword: 'bubble coral', category: 'LPS', subcategory: 'Bubble Corals' },
  { keyword: 'galaxea', category: 'LPS', subcategory: 'Galaxea' },
  { keyword: 'sun coral', category: 'LPS', subcategory: 'Sun Corals' },
  // Soft coral subcategories
  { keyword: 'toadstool', category: 'Soft Corals', subcategory: 'Toadstools' },
  { keyword: 'kenya tree', category: 'Soft Corals', subcategory: 'Kenya Trees' },
  { keyword: 'xenia', category: 'Soft Corals', subcategory: 'Xenia' },
  { keyword: 'gorgonian', category: 'Soft Corals', subcategory: 'Gorgonians' },
  { keyword: 'star polyp', category: 'Soft Corals', subcategory: 'Star Polyps' },
  { keyword: 'gsp', category: 'Soft Corals', subcategory: 'Star Polyps' },
  { keyword: 'clove polyp', category: 'Soft Corals', subcategory: 'Clove Polyps' },
  { keyword: 'pipe organ', category: 'Soft Corals', subcategory: 'Pipe Organs' },
];

/**
 * Examine the coral name for keywords that strongly signal a category
 * and/or subcategory. Used as a fallback/correction after column-based
 * matching.
 *
 * Rules:
 * - Only CORRECTS category if the name keyword disagrees with current value
 *   (e.g. "mushroom" in name but category says Soft Corals → fix to Mushrooms)
 * - Only FILLS subcategory if currently empty (never overrides user data)
 * - Returns null for category/subcategory when no change is needed
 */
export function inferFromName(
  name: string,
  currentCategory: AutoFixResult | null,
  currentSubcategory: AutoFixResult | null,
): { category: AutoFixResult | null; subcategory: AutoFixResult | null } {
  const nameLower = name.toLowerCase();

  let inferredCategory: string | null = null;
  let inferredSubcategory: string | null = null;

  // Find the first (most specific) matching keyword.
  // The array is ordered so multi-word entries like "candy cane" and "bubble tip"
  // appear before shorter keywords.
  for (const entry of NAME_CATEGORY_KEYWORDS) {
    // Word-boundary-ish check: keyword must appear as a standalone segment
    const idx = nameLower.indexOf(entry.keyword);
    if (idx === -1) continue;

    // Check it's not part of a larger word (simple boundary check)
    const before = idx > 0 ? nameLower[idx - 1] : ' ';
    const after = idx + entry.keyword.length < nameLower.length
      ? nameLower[idx + entry.keyword.length]
      : ' ';
    const isWordBoundary = /[\s\-_,.(]/.test(before!) || idx === 0;
    const isEndBoundary = /[\s\-_,.)s]/.test(after!) || (idx + entry.keyword.length === nameLower.length);

    if (!isWordBoundary || !isEndBoundary) continue;

    inferredCategory = entry.category;
    inferredSubcategory = entry.subcategory ?? null;
    break;
  }

  // Build return values

  // Category: correct if name-inference disagrees with current value, or fill if empty
  let categoryResult: AutoFixResult | null = null;
  if (inferredCategory) {
    const currentCatValue = currentCategory?.value ?? '';
    if (!currentCatValue) {
      // No category set — fill it in
      categoryResult = {
        value: inferredCategory,
        originalValue: null,
        confidence: 'mapped',
      };
    } else if (currentCatValue !== inferredCategory) {
      // Current category disagrees with name — correct it
      categoryResult = {
        value: inferredCategory,
        originalValue: currentCatValue,
        confidence: 'mapped',
      };
    }
    // If current matches inferred, no change needed → null
  }

  // Subcategory: only fill if currently empty, never override
  let subcategoryResult: AutoFixResult | null = null;
  if (inferredSubcategory) {
    const currentSubValue = currentSubcategory?.value ?? '';
    if (!currentSubValue) {
      subcategoryResult = {
        value: inferredSubcategory,
        originalValue: null,
        confidence: 'mapped',
      };
    }
    // If subcategory already has a value (even a wrong one), don't override
  }

  return { category: categoryResult, subcategory: subcategoryResult };
}

// Build a lowercase lookup of all known categories
const CATEGORY_NAMES_LOWER = new Map<string, string>();
for (const cat of Object.keys(CORAL_CATEGORIES)) {
  CATEGORY_NAMES_LOWER.set(cat.toLowerCase(), cat);
}

// Build a flat set of all subcategories (lowercase → canonical) and also
// track which category each belongs to
interface SubcategoryInfo {
  canonical: string;
  category: string;
}
const ALL_SUBCATEGORIES_LOWER = new Map<string, SubcategoryInfo>();
for (const [cat, subs] of Object.entries(CORAL_CATEGORIES)) {
  for (const sub of subs) {
    ALL_SUBCATEGORIES_LOWER.set(sub.toLowerCase(), { canonical: sub, category: cat });
  }
}

// ---------------------------------------------------------------------------
// Matching functions
// ---------------------------------------------------------------------------

/**
 * Match a raw category string against the known coral categories.
 * Returns the AutoFixResult and optionally a subcategory if the input was
 * reclassified (e.g. "Torch" → LPS + Torches).
 */
function matchCategory(raw: string): {
  category: AutoFixResult;
  reclassifiedSubcategory: string | null;
} {
  const trimmed = raw.trim();
  if (!trimmed) {
    return {
      category: { value: '', originalValue: null, confidence: 'flagged' },
      reclassifiedSubcategory: null,
    };
  }

  const lower = trimmed.toLowerCase();

  // Exact match (case-sensitive)
  if (CORAL_CATEGORIES[trimmed]) {
    return {
      category: { value: trimmed, originalValue: null, confidence: 'exact' },
      reclassifiedSubcategory: null,
    };
  }

  // Case-insensitive match against known category names
  const ciMatch = CATEGORY_NAMES_LOWER.get(lower);
  if (ciMatch) {
    return {
      category: {
        value: ciMatch,
        originalValue: trimmed !== ciMatch ? trimmed : null,
        confidence: trimmed === ciMatch ? 'exact' : 'mapped',
      },
      reclassifiedSubcategory: null,
    };
  }

  // Synonym map
  const synonym = CATEGORY_SYNONYMS[lower];
  if (synonym) {
    return {
      category: { value: synonym, originalValue: trimmed, confidence: 'mapped' },
      reclassifiedSubcategory: null,
    };
  }

  // Reclassify (input is actually a subcategory name used as category)
  const reclassify = RECLASSIFY_MAP[lower];
  if (reclassify) {
    return {
      category: {
        value: reclassify.category,
        originalValue: trimmed,
        confidence: 'mapped',
      },
      reclassifiedSubcategory: reclassify.subcategory,
    };
  }

  // No match → flagged
  return {
    category: { value: trimmed, originalValue: null, confidence: 'flagged' },
    reclassifiedSubcategory: null,
  };
}

/**
 * Match a raw subcategory string against known subcategories within a given
 * category (or across all categories if none specified).
 */
function matchSubcategory(
  raw: string,
  categoryValue: string,
): AutoFixResult {
  const trimmed = raw.trim();
  if (!trimmed) {
    return { value: '', originalValue: null, confidence: 'exact' };
  }

  const lower = trimmed.toLowerCase();
  const categorySubcategories = CORAL_CATEGORIES[categoryValue] ?? [];

  // Build lowercase lookup for the specific category
  const catSubsLower = new Map<string, string>();
  for (const sub of categorySubcategories) {
    catSubsLower.set(sub.toLowerCase(), sub);
  }

  // Exact match (case-sensitive)
  if (catSubsLower.has(lower) && catSubsLower.get(lower) === trimmed) {
    return { value: trimmed, originalValue: null, confidence: 'exact' };
  }

  // Case-insensitive match within category
  const ciMatch = catSubsLower.get(lower);
  if (ciMatch) {
    return {
      value: ciMatch,
      originalValue: trimmed !== ciMatch ? trimmed : null,
      confidence: trimmed === ciMatch ? 'exact' : 'mapped',
    };
  }

  // Abbreviation map
  const abbrev = SUBCATEGORY_ABBREVIATIONS[lower];
  if (abbrev) {
    // Verify the abbreviation target exists in the current category
    if (catSubsLower.has(abbrev.toLowerCase())) {
      return { value: abbrev, originalValue: trimmed, confidence: 'mapped' };
    }
    // If not in category but exists globally, still use it
    const globalInfo = ALL_SUBCATEGORIES_LOWER.get(abbrev.toLowerCase());
    if (globalInfo) {
      return { value: globalInfo.canonical, originalValue: trimmed, confidence: 'mapped' };
    }
  }

  // Contains match within category (e.g. "Favia" → "Favia/Favites")
  for (const sub of categorySubcategories) {
    if (sub.toLowerCase().includes(lower) || lower.includes(sub.toLowerCase())) {
      return { value: sub, originalValue: trimmed, confidence: 'mapped' };
    }
  }

  // Try across all categories (in case the user picked the wrong category)
  const globalMatch = ALL_SUBCATEGORIES_LOWER.get(lower);
  if (globalMatch) {
    return {
      value: globalMatch.canonical,
      originalValue: trimmed,
      confidence: 'mapped',
    };
  }

  // Contains match across all subcategories
  for (const [subLower, info] of ALL_SUBCATEGORIES_LOWER) {
    if (subLower.includes(lower) || lower.includes(subLower)) {
      return { value: info.canonical, originalValue: trimmed, confidence: 'mapped' };
    }
  }

  // No match → flagged with original value preserved
  return { value: trimmed, originalValue: null, confidence: 'flagged' };
}

/**
 * Match a raw status string to one of the canonical statuses.
 */
function matchStatus(raw: string): AutoFixResult {
  const trimmed = raw.trim();
  const lower = trimmed.toLowerCase();

  // Direct match (already canonical)
  if (lower === 'alive' || lower === 'deceased' || lower === 'sold' || lower === 'unknown') {
    return {
      value: lower,
      originalValue: trimmed !== lower ? trimmed : null,
      confidence: trimmed === lower ? 'exact' : 'mapped',
    };
  }

  // Synonym match
  const synonym = STATUS_SYNONYMS[lower];
  if (synonym !== undefined) {
    // Empty string maps to "unknown"
    const wasEmpty = trimmed === '';
    return {
      value: synonym,
      originalValue: wasEmpty ? null : trimmed,
      confidence: wasEmpty ? 'exact' : 'mapped',
    };
  }

  // No match → flagged
  return { value: trimmed, originalValue: null, confidence: 'flagged' };
}

// ---------------------------------------------------------------------------
// Date parsing
// ---------------------------------------------------------------------------

/**
 * Parse a date string into YYYY-MM-DD format.
 * Handles common formats: MM/DD/YYYY, DD/MM/YYYY, YYYY-MM-DD, etc.
 */
function parseDate(raw: string, format: DateFormatPreference): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;

  // Already ISO format (YYYY-MM-DD)
  const isoMatch = /^(\d{4})-(\d{1,2})-(\d{1,2})/.exec(trimmed);
  if (isoMatch) {
    const [, y, m, d] = isoMatch;
    return `${y}-${pad2(m!)}-${pad2(d!)}`;
  }

  // Slash or dash delimited: could be MM/DD/YYYY or DD/MM/YYYY
  const slashMatch = /^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{2,4})$/.exec(trimmed);
  if (slashMatch) {
    const [, part1, part2, yearPart] = slashMatch;
    const year = normalizeYear(yearPart!);

    if (format === 'EU') {
      // DD/MM/YYYY
      return `${year}-${pad2(part2!)}-${pad2(part1!)}`;
    }
    // US (default): MM/DD/YYYY
    return `${year}-${pad2(part1!)}-${pad2(part2!)}`;
  }

  // Try native Date parsing as fallback (handles "Jan 5, 2025" etc.)
  const parsed = new Date(trimmed);
  if (!isNaN(parsed.getTime())) {
    const y = parsed.getFullYear();
    const m = parsed.getMonth() + 1;
    const d = parsed.getDate();
    return `${y}-${pad2(String(m))}-${pad2(String(d))}`;
  }

  return null;
}

function pad2(s: string): string {
  return s.length === 1 ? `0${s}` : s;
}

function normalizeYear(yearStr: string): string {
  if (yearStr.length === 4) return yearStr;
  // 2-digit year: assume 2000s for 00-49, 1900s for 50-99
  const num = parseInt(yearStr, 10);
  return num < 50 ? `20${pad2(yearStr)}` : `19${pad2(yearStr)}`;
}

// ---------------------------------------------------------------------------
// Price parsing
// ---------------------------------------------------------------------------

/**
 * Parse a price string, stripping currency symbols and commas.
 * Returns null if the value is empty or unparseable.
 */
function parsePrice(raw: string): number | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;

  // Strip common currency symbols and whitespace
  const cleaned = trimmed.replace(/[$£€A$C$\s,]/g, '');
  if (!cleaned) return null;

  const num = parseFloat(cleaned);
  return isNaN(num) ? null : num;
}

// ---------------------------------------------------------------------------
// Live issue recomputation
// ---------------------------------------------------------------------------

/**
 * Derive the current list of issues from a row's live state.
 *
 * Unlike the static `row.issues` array (computed once at parse time), this
 * function re-evaluates based on the *current* field values and confidence
 * levels so that issues disappear as soon as the user fixes them.
 */
export function computeRowIssues(row: ImportRow): string[] {
  const issues: string[] = [];

  if (!row.name) {
    issues.push('Name is required');
  }

  // Flagged (unrecognised) values
  if (row.category?.confidence === 'flagged' && row.category.value) {
    issues.push(`Unknown category: "${row.category.value}"`);
  }

  if (row.subcategory?.confidence === 'flagged' && row.subcategory.value) {
    issues.push(`Unknown subcategory: "${row.subcategory.value}"`);
  }

  if (row.status?.confidence === 'flagged') {
    issues.push(`Unknown status: "${row.status.value}"`);
  }

  // Empty fields that should be filled in
  if (!row.category || !row.category.value) {
    issues.push('Category is empty');
  }

  if (!row.subcategory || !row.subcategory.value) {
    issues.push('Subcategory is empty');
  }

  // Keep original date/price parse issues only if still unresolved
  for (const issue of row.issues) {
    if (issue.startsWith('Could not parse date') && !row.acquisitionDate) {
      issues.push(issue);
    }
    if (issue.startsWith('Could not parse price') && row.price === null) {
      issues.push(issue);
    }
  }

  return issues;
}

// ---------------------------------------------------------------------------
// Main processing function
// ---------------------------------------------------------------------------

/**
 * Process raw spreadsheet rows through column mappings and the matching engine.
 *
 * For each row, extracts values based on mappings, runs auto-fix logic for
 * category/subcategory/status, parses dates and prices, and returns
 * ImportRow[] with issues listed.
 */
export function processImportRows(
  rawRows: Record<string, string>[],
  mappings: ColumnMapping[],
  dateFormat: DateFormatPreference,
): ImportRow[] {
  // Build a lookup: targetField → sourceColumn
  const fieldToColumn = new Map<TargetField, string>();
  for (const mapping of mappings) {
    if (mapping.targetField !== 'skip') {
      fieldToColumn.set(mapping.targetField, mapping.sourceColumn);
    }
  }

  const getValue = (row: Record<string, string>, field: TargetField): string => {
    const col = fieldToColumn.get(field);
    if (!col) return '';
    return row[col] ?? '';
  };

  // Track names for duplicate detection
  const nameCount = new Map<string, number>();
  for (const row of rawRows) {
    const name = getValue(row, 'name').trim().toLowerCase();
    if (name) {
      nameCount.set(name, (nameCount.get(name) ?? 0) + 1);
    }
  }

  return rawRows.map((row, index) => {
    const issues: string[] = [];

    // Name (required)
    const name = getValue(row, 'name').trim();
    if (!name) {
      issues.push('Name is required');
    }

    // Category matching (only for coral-type — for now we always try)
    let category: AutoFixResult | null = null;
    let subcategory: AutoFixResult | null = null;

    const rawCategory = getValue(row, 'category');
    const rawSubcategory = getValue(row, 'subcategory');

    if (rawCategory) {
      const catResult = matchCategory(rawCategory);
      category = catResult.category;

      if (catResult.category.confidence === 'flagged' && catResult.category.value) {
        issues.push(`Unknown category: "${catResult.category.value}"`);
      }

      // Handle subcategory — if category was reclassified, use that subcategory
      // unless the user already provided one
      if (rawSubcategory) {
        subcategory = matchSubcategory(rawSubcategory, category.value);
        if (subcategory.confidence === 'flagged' && subcategory.value) {
          issues.push(`Unknown subcategory: "${subcategory.value}"`);
        }
      } else if (catResult.reclassifiedSubcategory) {
        subcategory = {
          value: catResult.reclassifiedSubcategory,
          originalValue: rawCategory.trim(),
          confidence: 'mapped',
        };
      }
    } else if (rawSubcategory) {
      // Subcategory without category — try matching globally
      subcategory = matchSubcategory(rawSubcategory, '');
      if (subcategory.confidence === 'flagged' && subcategory.value) {
        issues.push(`Unknown subcategory: "${subcategory.value}"`);
      }
    }

    // --- Name-based inference (fills gaps, corrects miscategorisation) ---
    const nameInference = inferFromName(name, category, subcategory);
    if (nameInference.category) {
      // Track the correction in issues if category was changed (not just filled)
      if (category && category.value && category.value !== nameInference.category.value) {
        // Remove the old "Unknown category" issue if present — the name told us the answer
        const unknownCatIdx = issues.findIndex((i) => i.startsWith('Unknown category'));
        if (unknownCatIdx !== -1) issues.splice(unknownCatIdx, 1);
      }
      category = nameInference.category;
    }
    if (nameInference.subcategory) {
      subcategory = nameInference.subcategory;
    }

    // Status matching
    let status: AutoFixResult | null = null;
    const rawStatus = getValue(row, 'status');
    if (fieldToColumn.has('status')) {
      status = matchStatus(rawStatus);
      if (status.confidence === 'flagged') {
        issues.push(`Unknown status: "${status.value}"`);
      }
    }

    // Date parsing
    let acquisitionDate: string | null = null;
    const rawDate = getValue(row, 'acquisition_date');
    if (rawDate) {
      acquisitionDate = parseDate(rawDate, dateFormat);
      if (!acquisitionDate) {
        issues.push(`Could not parse date: "${rawDate}"`);
      }
    }

    // Price parsing
    let price: number | null = null;
    const rawPrice = getValue(row, 'price');
    if (rawPrice) {
      price = parsePrice(rawPrice);
      if (price === null && rawPrice.trim()) {
        issues.push(`Could not parse price: "${rawPrice}"`);
      }
    }

    // Simple fields
    const vendor = getValue(row, 'vendor').trim() || null;
    const notes = getValue(row, 'notes').trim() || null;
    const scientificName = getValue(row, 'scientific_name').trim() || null;

    // Duplicate detection (by name, case-insensitive)
    const lowerName = name.toLowerCase();
    const isDuplicate = !!lowerName && (nameCount.get(lowerName) ?? 0) > 1;

    return {
      _rowIndex: index,
      name,
      category,
      subcategory,
      vendor,
      price,
      acquisitionDate,
      status,
      notes,
      scientificName,
      isDuplicate,
      isSkipped: false,
      issues,
    };
  });
}
